import { Dispatch } from 'redux';
import { api } from "../config/api";
import { GetState } from "../config/store";
import {
    downloadUsedTemplateRequest,
    downloadUsedTemplateSuccess,
    downloadUsedTemplateFailure,
    downloadNewTemplateRequest,
    downloadNewTemplateSuccess,
    downloadNewTemplateFailure,
    bulkUploadRequest,
    bulkUploadSuccess,
    bulkUploadFailure,
    bulkUploadStorageRequest,
    bulkUploadStorageSuccess,
    bulkUploadStorageFailure,
    validateUploadRequest,
    validateUploadSuccess,
    validateUploadFailure,
    bulkStorageConfirmRequest,
    updateUploadProgress,
    bulkStorageConfirmSuccess,
    bulkStorageConfirmFailure,
    cleanMassiveCharge as cleanMassiveChargeReducer
} from '../reducers/massiveCharge';
import { axiosClient } from '../config/axios';
import { catch401 } from '../helpers/actions';


export const downloadUsedVehicleTemplate = () => (dispatch: Dispatch) => _downloadUsedVehicleTemplate(dispatch);
const _downloadUsedVehicleTemplate = async (dispatch: Dispatch) => {
    try {
        dispatch({ type: downloadUsedTemplateRequest });

        const response = await axiosClient.get(`${api.settings}/parameters/process_publication_used`);

        dispatch({ type: downloadUsedTemplateSuccess, payload: response?.data });
        return response?.data?.data; // Return the data.parameter object

    } catch (error: any) {
        const errorResponse = error?.response?.data?.error;
        catch401(errorResponse?.code, dispatch);

        console.log('Error downloading used vehicle template:', errorResponse?.code);
        dispatch({ type: downloadUsedTemplateFailure, payload: errorResponse?.code });
        throw error;
    }
};


export const downloadNewVehicleTemplate = () => (dispatch: Dispatch) => _downloadNewVehicleTemplate(dispatch);
const _downloadNewVehicleTemplate = async (dispatch: Dispatch) => {
    try {
        dispatch({ type: downloadNewTemplateRequest });

        const response = await axiosClient.get(`${api.settings}/parameters/process_publication_new`);

        dispatch({ type: downloadNewTemplateSuccess, payload: response?.data });
        return response?.data?.data; // Return the data.parameter object

    } catch (error: any) {
        const errorResponse = error?.response?.data?.error;
        catch401(errorResponse?.code, dispatch);

        console.log('Error downloading new vehicle template:', errorResponse?.code);
        dispatch({ type: downloadNewTemplateFailure, payload: errorResponse?.code });
        throw error;
    }
};


export const bulkUploadVehicles = (file: File, vehicleType: 'new' | 'used', replacement?: boolean) => (dispatch: Dispatch) => _bulkUploadVehicles(dispatch, file, vehicleType, replacement);
const _bulkUploadVehicles = async (dispatch: Dispatch, file: File, vehicleType: 'new' | 'used', replacement?: boolean) => {
    try {
        dispatch({ type: bulkUploadRequest });

        const formData = new FormData();
        formData.append('file', file);
        formData.append('type', vehicleType);
        formData.append('replacement', replacement ? 'true' : 'false');

        const response = await axiosClient.post(`${api.publications}/process-publications/bulk-upload`, formData);

        dispatch({ type: bulkUploadSuccess, payload: response?.data });
        return response?.data;

    } catch (error: any) {
        const errorResponse = error?.response?.data?.error;
        catch401(errorResponse?.code, dispatch);

        console.log('Error uploading bulk vehicles:', errorResponse?.code);
        dispatch({ type: bulkUploadFailure, payload: errorResponse?.code });
        throw error;
    }
};


export const bulkUploadImages = (file: File, vehicleType: 'new' | 'used', archiveId: string, replacement: boolean = false) =>
    (dispatch: Dispatch) => _bulkUploadImages(dispatch, file, vehicleType, archiveId, replacement);

const _bulkUploadImages = async (
    dispatch: Dispatch,
    file: File,
    vehicleType: 'new' | 'used',
    archiveId: string,
    replacement: boolean
) => {
    try {
        dispatch({ type: bulkUploadRequest });
        // Establecer progreso a 0% inmediatamente al iniciar
        dispatch(updateUploadProgress(0));

        const formData = new FormData();
        formData.append('file', file);
        formData.append('replacement_images', replacement ? 'true' : 'false');

        const response = await axiosClient.get(`${api.publications}/storage/signed-url?filename=${file.name}&contentType=application%2Fzip`);

        dispatch({ type: bulkUploadSuccess, payload: response?.data });
        return response?.data;

    } catch (error: any) {
        const errorResponse = error?.response?.data?.error;
        catch401(errorResponse?.code, dispatch);

        console.log('Error uploading bulk images:', errorResponse?.code);
        dispatch({ type: bulkUploadFailure, payload: errorResponse?.code });
        throw error;
    }
};

export const bulkUploadImagesStorage = (file: File, url: string) =>
    (dispatch: Dispatch) => _bulkUploadImagesStorage(dispatch, file, url);

const _bulkUploadImagesStorage = async (
    dispatch: Dispatch,
    file: File,
    url: string
) => {
    try {
        dispatch({ type: bulkUploadStorageRequest });
        // Reset progress to 0 when starting upload
        dispatch(updateUploadProgress(0));
        
        const result = await new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open('PUT', url, true);
            xhr.setRequestHeader('Content-Type', 'application/zip');
            
            // Track upload progress
            xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                    const percentComplete = Math.round((event.loaded / event.total) * 100);
                    dispatch(updateUploadProgress(percentComplete));
                }
            };
            
            xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    // Set progress to 100% when complete
                    dispatch(updateUploadProgress(100));
                    resolve({ data: xhr.response });
                } else {
                    reject({
                        status: xhr.status,
                        statusText: xhr.statusText,
                        response: xhr.response
                    });
                }
            };
            
            xhr.onerror = () => {
                // Reset progress on error
                dispatch(updateUploadProgress(0));
                reject({
                    status: xhr.status,
                    statusText: xhr.statusText,
                    response: xhr.response
                });
            };
            
            // Add abort handler
            xhr.onabort = () => {
                dispatch(updateUploadProgress(0));
                reject({
                    status: 0,
                    statusText: 'Upload aborted',
                    response: null
                });
            };
            
            xhr.send(file);
        });
        
        dispatch({ type: bulkUploadStorageSuccess, payload: result });

        return result;
    } catch (error: any) {
        console.log("Error uploading bulk images:", {
            status: error?.status,
            statusText: error?.statusText,
            response: error?.response,
            fullError: error
        });
        dispatch({ type: bulkUploadStorageFailure, payload: error?.statusText || 'Upload failed' });
        throw error;
    }
};

export const confirmUploadProcess = (processId: string, filePath: string, replacement_images: boolean = false) => (
    dispatch: Dispatch
) => _confirmUploadProcess(dispatch, processId, filePath, replacement_images);

const _confirmUploadProcess = async (
    dispatch: Dispatch,
    processId: string,
    filePath: string,
    replacement_images: boolean
) => {
    try {
        dispatch({ type: bulkStorageConfirmRequest });
        
        const response = await axiosClient.post(
            `${api.publications}/process-publications/confirm`,
            {
                processId,
                filePath,
                replacement_images
            }
        );
        
        dispatch({ type: bulkStorageConfirmSuccess, payload: response.data });
        return response.data;
    } catch (error: any) {
        dispatch({ type: bulkStorageConfirmFailure, payload: error?.statusText || 'Confirmation failed' });
        throw error;
    }
};

export const validateUploadStatus = (archiveId: string) => (dispatch: Dispatch) => _validateUploadStatus(dispatch, archiveId);
const _validateUploadStatus = async (dispatch: Dispatch, archiveId: string) => {
    try {
        dispatch({ type: validateUploadRequest });
        // Establecer progreso a 0% al iniciar la validación
        dispatch(updateUploadProgress(0));

        console.log('🔍 Validating upload status for archiveId:', archiveId);
        const response = await axiosClient.get(`${api.publications}/process-publications/${archiveId}`);

        console.log('✅ Validation response:', response.data);
        dispatch({ type: validateUploadSuccess, payload: response?.data });
        return response?.data;

    } catch (error: any) {
        const errorResponse = error?.response?.data?.error;
        catch401(errorResponse?.code, dispatch);

        console.error('❌ Error validating upload status:', error);
        dispatch({ type: validateUploadFailure, payload: errorResponse?.code || 'Error validating upload status' });
        throw error;
    }
};

export const cleanMassiveCharge = () => (dispatch: Dispatch) => _cleanMassiveCharge(dispatch);
const _cleanMassiveCharge = (dispatch: Dispatch) => dispatch({ type: cleanMassiveChargeReducer });